import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { getToken, getUser } from './utils/api';

// Areas whose sub-tabs swap only the content below a shared header (About HB: clock + map) —
// switching tabs there keeps the scroll position instead of jumping to the top.
const KEEP_SCROLL_AREAS = ['hbworld'];

function ScrollToTop() {
  const { pathname } = useLocation();
  const prev = useRef(pathname);
  useEffect(() => {
    const area = (p) => p.split('/')[1];
    const sameArea = area(prev.current) === area(pathname) && KEEP_SCROLL_AREAS.includes(area(pathname));
    prev.current = pathname;
    if (!sameArea) window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Public
import EntryPage from './pages/public/EntryPage';

// Auth
import LoginPage from './pages/auth/LoginPage';

// Areas
import HBWorld from './pages/hbworld/HBWorld';
import Deliver, { ProgrammeDetail } from './pages/deliver/DeliverPages';
import Community from './pages/community/CommunityPages';
import OngoingPages from './pages/ongoing/OngoingPages';
import NewsPages from './pages/news/NewsPages';
import HikeAdmin from './pages/community/HikeAdminPage';
import Grow from './pages/grow/GrowPages';
import Explore from './pages/explore/ExplorePages';
import Master from './pages/master/MasterPages';
import Contribute from './pages/contribute/ContributePages';
import { Journey, Formation, Assessments, MyClass } from './pages/cfa/CandidatePages';
import { Recruitment, CfaLead, GrowthLead } from './pages/cfa/PipelinePages';
import ComingSoon from './pages/ComingSoon';
import CustomersPages from './pages/customers/CustomersPages';
import UserManagement from './pages/admin/UserManagement';
import MyLine from './pages/home/MyLine';
import Assistant from './pages/assistant/Assistant';
import ProcessDesigner from './pages/admin/ProcessDesigner';
import AIUsage from './pages/admin/AIUsage';
import SupportPage from './pages/support/SupportPage';

// Where a signed-in user lands (WI-C5): IP → My Line; Candidate → journey;
// governance/admin-only → their workplace; HB World is always one click away.
function landingPath() {
  const roles = getUser()?.roles || [];
  if (roles.includes('IP')) return '/home';
  if (roles.includes('CAND')) return '/journey';
  if (roles.includes('SUPERADMIN')) return '/admin/users';
  if (roles.includes('ADM')) return '/operations';
  if (roles.includes('SH')) return '/governance';
  if (roles.includes('BP')) return '/exchange';
  return '/hbworld';
}

// ── Auth guard ─────────────────────────────────────────────────────────────
function RequireAuth({ children }) {
  if (!getToken()) return <Navigate to="/login" replace />;
  return children;
}

function RedirectIfAuth({ children }) {
  if (getToken()) return <Navigate to={landingPath()} replace />;
  return children;
}

// Samo SUPERADMIN sme na admin rute (defense-in-depth uz backend gating).
function RequireSuperadmin({ children }) {
  if (!getToken()) return <Navigate to="/login" replace />;
  const roles = getUser()?.roles || [];
  if (!roles.includes('SUPERADMIN')) return <Navigate to="/hbworld" replace />;
  return children;
}

function LandingGuard() {
  if (getToken()) return <Navigate to={landingPath()} replace />;
  return <EntryPage />;
}

// Legacy /crm/:sub → /customers/:sub (projects tab je preimenovan u journeys).
function CrmRedirect() {
  const { sub } = useParams();
  const map = { projects: 'journeys', funnel: 'funnel', week: 'week', clients: 'clients' };
  return <Navigate to={`/customers/${map[sub] || 'journeys'}`} replace />;
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <ScrollToTop />
      <Routes>
        {/* Public entry page */}
        <Route path="/" element={<LandingGuard />} />

        {/* Auth */}
        <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />

        {/* My Line — home (WI-C5). Hansen Beck Toolbox je premešten u Grow → Content Atlas. */}
        <Route path="/home" element={<RequireAuth><MyLine /></RequireAuth>} />
        {/* Hansen Beck assistant — AI chat (read-only; paid, request → grant) */}
        <Route path="/assistant" element={<RequireAuth><Assistant /></RequireAuth>} />
        {/* Legacy redirecti: stari Toolbox/Songbook linkovi → Grow → Content Atlas */}
        <Route path="/toolbox" element={<RequireAuth><Navigate to="/grow/atlas" replace /></RequireAuth>} />
        <Route path="/songbook" element={<RequireAuth><Navigate to="/grow/atlas" replace /></RequireAuth>} />

        {/* Support — How to use + FAQ */}
        <Route path="/support" element={<RequireAuth><SupportPage /></RequireAuth>} />

        {/* Hansen Beck */}
        <Route path="/hbworld" element={<RequireAuth><HBWorld /></RequireAuth>} />
        <Route path="/hbworld/:sub" element={<RequireAuth><HBWorld /></RequireAuth>} />

        {/* Deliver — živ u Fazi 1 */}
        <Route path="/deliver" element={<RequireAuth><Deliver /></RequireAuth>} />
        {/* Unit record renders inside the Deliver shell (keeps the Deliver submenu) */}
        <Route path="/deliver/units/:code" element={<RequireAuth><Deliver /></RequireAuth>} />
        <Route path="/deliver/progs/:id" element={<RequireAuth><ProgrammeDetail /></RequireAuth>} />
        <Route path="/deliver/:sub" element={<RequireAuth><Deliver /></RequireAuth>} />

        {/* Catalogue (teaser) — kandidati videx isti katalog, backend filtrira na tier 1 */}
        <Route path="/catalogue" element={<RequireAuth><Navigate to="/deliver/units" replace /></RequireAuth>} />

        {/* Community */}
        <Route path="/community" element={<RequireAuth><Community /></RequireAuth>} />
        <Route path="/community/:sub" element={<RequireAuth><Community /></RequireAuth>} />

        {/* Ongoing Things */}
        <Route path="/ongoing" element={<RequireAuth><OngoingPages /></RequireAuth>} />
        <Route path="/ongoing/:sub" element={<RequireAuth><OngoingPages /></RequireAuth>} />

        {/* News & Events */}
        <Route path="/news" element={<RequireAuth><NewsPages /></RequireAuth>} />
        <Route path="/news/:sub" element={<RequireAuth><NewsPages /></RequireAuth>} />
        {/* WI-X1: /events route → the Events sign-up view */}
        <Route path="/events" element={<RequireAuth><Navigate to="/news/events" replace /></RequireAuth>} />

        {/* Grow (Faza 2) */}
        <Route path="/grow" element={<RequireAuth><Grow /></RequireAuth>} />
        <Route path="/grow/:sub" element={<RequireAuth><Grow /></RequireAuth>} />

        {/* Kandidat (Faza 2) */}
        <Route path="/journey" element={<RequireAuth><Journey /></RequireAuth>} />
        <Route path="/formation" element={<RequireAuth><Formation /></RequireAuth>} />
        <Route path="/my-assessments" element={<RequireAuth><Assessments /></RequireAuth>} />
        <Route path="/my-class" element={<RequireAuth><MyClass /></RequireAuth>} />

        {/* CFA pipeline radna mesta (Faza 2) */}
        <Route path="/recruitment" element={<RequireAuth><Recruitment /></RequireAuth>} />
        <Route path="/cfalead" element={<RequireAuth><CfaLead /></RequireAuth>} />
        <Route path="/growthlead" element={<RequireAuth><GrowthLead /></RequireAuth>} />
        <Route path="/hikeadmin" element={<RequireAuth><HikeAdmin /></RequireAuth>} />

        {/* Explore (Faza 3A) */}
        <Route path="/explore" element={<RequireAuth><Explore /></RequireAuth>} />
        <Route path="/explore/:sub" element={<RequireAuth><Explore /></RequireAuth>} />

        {/* Master (Faza 3B) */}
        <Route path="/master" element={<RequireAuth><Master /></RequireAuth>} />
        <Route path="/master/:sub" element={<RequireAuth><Master /></RequireAuth>} />

        {/* Contribute */}
        <Route path="/contribute" element={<RequireAuth><Contribute /></RequireAuth>} />
        <Route path="/contribute/:sub" element={<RequireAuth><Contribute /></RequireAuth>} />

        {/* Admin — User Management (samo SUPERADMIN) */}
        <Route path="/admin/users" element={<RequireSuperadmin><UserManagement /></RequireSuperadmin>} />
        <Route path="/admin/process" element={<RequireAuth><ProcessDesigner /></RequireAuth>} />
        <Route path="/admin/ai-usage" element={<RequireAuth><AIUsage /></RequireAuth>} />

        {/* My Customers — the single workplace (was: CRM) */}
        <Route path="/customers" element={<RequireAuth><CustomersPages /></RequireAuth>} />
        <Route path="/customers/:sub" element={<RequireAuth><CustomersPages /></RequireAuth>} />

        {/* Legacy CRM redirecti — stari bookmarks nastavljaju da rade */}
        <Route path="/crm" element={<RequireAuth><Navigate to="/customers" replace /></RequireAuth>} />
        <Route path="/crm/:sub" element={<RequireAuth><CrmRedirect /></RequireAuth>} />

        {/* Oblasti koje dolaze u kasnijim fazama — sidebar ih već prikazuje */}
        <Route path="/exchange" element={<RequireAuth><ComingSoon title="Strategy & Projects" subtitle="Business Partner exchange" phase="Phase 4" message="HB strategy, project register, business report & review, territory and team." /></RequireAuth>} />
        <Route path="/operations" element={<RequireAuth><ComingSoon title="Operations" subtitle="Administration" phase="Phase 4" message="Sales templates and documents, calendars and events logistics." /></RequireAuth>} />
        <Route path="/governance" element={<RequireAuth><ComingSoon title="Governance" subtitle="Shareholders" phase="Phase 4" message="Certification process, Business Partners review, project decisions, GmbH performance." /></RequireAuth>} />
        <Route path="/sysadmin" element={<RequireAuth><ComingSoon title="System administration" subtitle="Function workplace" phase="Phase 4" message="Appoint functions and assignments in the name of HB GmbH, with audit history." /></RequireAuth>} />

        {/* 404 */}
        <Route path="*" element={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', background: 'var(--canvas)' }}>
            <div style={{ fontSize: '4rem', fontFamily: 'var(--font-display)', color: 'var(--ink-faint)' }}>404</div>
            <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--ink)' }}>Page not found</h2>
            <a href="/" style={{ color: 'var(--accent)', fontSize: '0.9rem' }}>← Back to home</a>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}
